import { useEffect, useMemo, useRef, useState } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import useOffers from 'api/useOffers';
import { Offer } from 'common/contracts';
import { OfferType } from 'constants/enums';
import isEmpty from 'lodash/isEmpty';
import { SettingsTabProps } from 'views/RollingOffers/types';

import Badge from 'components/Badge/Badge';
import Input from 'components/Input/Input';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import RichText from 'components/RichText/RichText';
import Typography from 'components/Typography/Typography';
import { Separator } from 'components/ui/separator';

const SettingsTab: React.FC<SettingsTabProps> = ({
  formikProps,
  rollingOfferId,
  edit,
  chosenSegment,
  setChosenSegment,
  offerDesignItems,
  data,
  dup
}) => {
  const { handleChange, handleBlur, touched, errors, setFieldValue, values } =
    formikProps;

  const [isNameExists, setIsNameExists] = useState<boolean>(false);
  const [segmentError, setSegmentError] = useState<string>('');
  const [inputValue, setInputValue] = useState('');

  const inputRef = useRef<HTMLInputElement | null>(null);

  const { getOffers } = useOffers({
    offerId: rollingOfferId,
    offerType: OfferType.ROLLING_OFFER
  });

  const offerDesignOptions = useMemo(() =>
    offerDesignItems?.map((item) => ({
      value: item.value,
      label: item.content
    })) || [],
    [offerDesignItems]
  );

  const selectedOfferDesign = useMemo(() => {
    const selected = offerDesignItems?.find((item) => item.value === values.offerUiId);
    return selected ? { value: selected.value, label: selected.content } : undefined;
  }, [offerDesignItems, values.offerUiId]);

  const isNameError = (touched.name && errors.name) || isNameExists;
  const nameErrorMessage = (touched.name ? errors.name?.toString() : '') || (isNameExists ? 'Name already exists' : '');

  const isExternalIdError = Boolean(errors.publisherOfferId) && (touched.publisherOfferId || dup);
  const externalIdErrorMessage = (touched.publisherOfferId || dup) ? errors.publisherOfferId?.toString() : '';

  const offerDesignErrorMessage = (touched.offerUiId && errors.offerUiId) ? errors.offerUiId?.toString() : '';

  useEffect(() => {
    const offerExists = getOffers.data?.offers.some(
      (offer: Offer) => offer.name === values.name
    );
    setIsNameExists(offerExists && !edit);
  }, [values.name, edit, getOffers.data]);

  useEffect(() => {
    if (data?.segments) {
      setChosenSegment(data.segments);
    }
  }, [data?.segments, setChosenSegment]);

  const handleAddSegment = (val: string) => {
    if (chosenSegment.includes(val)) {
      setSegmentError("You can't add the same segment twice");
    } else {
      const newSegments = [...chosenSegment, val];
      setChosenSegment(newSegments);
      setFieldValue('segments', newSegments);
      setSegmentError('');
      setInputValue('');
      inputRef.current?.blur();
    }
  };

  const handleRemoveSegment = (val: string) => {
    const newSegments = chosenSegment.filter(
      (segment: string) => segment !== val
    );
    setChosenSegment(newSegments);
    setFieldValue('segments', newSegments);
  };

  return (
    <div className="flex flex-col">
      <RichText
        title="General"
        description="Complete the required fields for your offer."
        className="mb-5"
      />
      <div className="flex flex-col gap-9">
        <div className="grid grid-cols-2 gap-5">
          <Input
            id="rolling-offer-name"
            label="Name"
            name="name"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            isRequired
            placeholder="Name"
            state={isNameError ? 'error' : 'default'}
            description={nameErrorMessage}
            dataTestId="rollingOfferNameInput"
          />
          <Input
            id="rolling-offer-external-id"
            label="External ID"
            name="publisherOfferId"
            value={values.publisherOfferId}
            onChange={handleChange}
            onBlur={handleBlur}
            isRequired
            placeholder="External ID"
            state={isExternalIdError ? 'error' : 'default'}
            description={externalIdErrorMessage}
            dataTestId="rollingOfferIdInput"
          />
        </div>
        <div className="grid grid-cols-2 gap-5">
          <InputSelectDropdown
            label="Offer Design"
            isRequired
            placeholder="Select"
            listDataTestId="dashRollingOfferDesignList"
            buttonDataTestId="rollingOfferDesignBox"
            options={offerDesignOptions}
            defaultSelected={selectedOfferDesign}
            onOptionChange={(value) => setFieldValue('offerUiId', value)}
            description={offerDesignErrorMessage}
          />
          <Input
            id="rolling-offer-priority"
            label="Priority"
            name="priority"
            value={values.priority}
            onChange={handleChange}
            onBlur={handleBlur}
            isRequired
            type="number"
            placeholder="1"
            min={1}
            max={10}
            infoTooltipContent="Priority decides which rolling offer show, based on their importance. The lower the number, the higher the priority."
          />
        </div>
      </div>
      <Separator className="my-5 bg-base-border" />
      <RichText
        title="Segments"
        description="Create or add players segments to your offer."
        className="mt-4 mb-5"
      />
      <div className="flex flex-col gap-5">
        <div>
          <Typography className="text-black font-medium text-sm mb-2">
            Segment Name
          </Typography>
          <Input
            ref={inputRef}
            value={inputValue}
            placeholder="Search or create new"
            dataTestId="rollingOfferSegmentsInput"
            rightIcon={
              <FontAwesomeIcon
                icon="fa-regular fa-magnifying-glass"
                className="w-4 h-4"
              />
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter' && inputValue.trim()) {
                e.preventDefault();
                handleAddSegment(inputValue.trim());
              }
            }}
            onChange={(e) => {
              setInputValue(e.target.value);
              setSegmentError(
                chosenSegment.includes(e.target.value)
                  ? "You can't add the same segment twice"
                  : ''
              );
            }}
            onBlur={handleBlur}
            state={segmentError ? 'error' : 'default'}
            description={segmentError || ''}
          />
          {isEmpty(chosenSegment) && (
            <Typography className="text-zinc-500 text-sm mt-2">
              Press 'Enter' to add a new segment.
            </Typography>
          )}
          {!isEmpty(chosenSegment) && (
            <div className="mt-3 flex flex-row flex-wrap gap-2" data-testid="rollingOfferSegmentsList">
              {chosenSegment.map((val: string) => (
                <Badge
                  key={val}
                  isRemovable
                  onRemove={() => handleRemoveSegment(val)}
                >
                  {val}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>
      <Separator className="my-5 bg-base-border" />
    </div>
  );
};

export default SettingsTab;
